/**
 * Everything that ever happened to the money.
 *
 * Two things here work differently from the server this was ported from, and
 * both for the same reason: the API cannot read a word of it.
 *
 * SEARCH is client-side. `server.py` ran `lower(t.reason) LIKE ?` in SQL, and
 * you cannot LIKE a ciphertext. Filtering after decryption also means the
 * search term never leaves this device.
 *
 * EXPORT is client-side. The server has no names to join onto rows, so the CSV
 * is built here and handed straight to the browser. It never exists anywhere
 * but where the parent saved it.
 */

import { FamilyMark } from '../components/FamilyMark';
import { Character } from '../avatar/Character';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { amendEntry, loadJournal, search, voidEntry, type Journal as JournalData } from '../family/journal';
import { entryParts, splitLine } from '../family/split';
import { buildCsv, downloadCsv, rowsFor } from '../family/csv';
import { amountInputMode, asTyped, dollars, howMuch, inLabel, looksLikeAmount, signed } from '../family/money';
import type { Entry, Goal, Person } from '../family/state';

export function Journal({
  familyId,
  familyKey,
  people,
  familyName,
  goals = [],
  onBack,
}: {
  familyId: string;
  familyKey: CryptoKey;
  people: Person[];
  familyName: string;
  /** For naming the goals automatic splits set money aside into. */
  goals?: Goal[];
  onBack?: () => void;
}) {
  const [data, setData] = useState<JournalData | null>(null);
  const [month, setMonth] = useState('');
  const [person, setPerson] = useState('');
  const [term, setTerm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [voiding, setVoiding] = useState<string | null>(null);
  /** The entry being corrected, if any. One at a time. */
  const [correcting, setCorrecting] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');

  const names = useMemo(
    () => new Map(people.map((one) => [one.id, one.name || 'Unnamed'])),
    [people],
  );

  const refresh = useCallback(async () => {
    setData(await loadJournal(familyId, familyKey, { month, person }));
  }, [familyId, familyKey, month, person]);

  useEffect(() => {
    void refresh().catch((problem: unknown) => {
      setError(problem instanceof Error ? problem.message : 'Something went wrong.');
    });
  }, [refresh]);

  const shown = useMemo(
    () => (data ? search(data.entries, term, names) : []),
    [data, term, names],
  );

  async function attempt(work: () => Promise<void>): Promise<boolean> {
    setError('');
    setBusy(true);
    try {
      await work();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Something went wrong.');
      setBusy(false);
      return false;
    }
    try {
      await refresh();
    } catch {
      setError('Saved, but the journal could not refresh. Reload this page to see the change.');
    } finally {
      setBusy(false);
    }
    return true;
  }

  return (
    <>
      <h2>Journal</h2>
      <p>
        Everything that ever happened, including what was taken back out.
        Nothing is ever deleted &mdash; every balance is worked out by adding
        these rows up, so removing one would change history silently.
      </p>

      <label className="field">
        <span>Search</span>
        <input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          autoComplete="off"
          placeholder="cinema, a name, tidied…"
        />
        <small>
          Searched on this device after decrypting. What you type here never
          leaves it.
        </small>
      </label>

      <div className="filters">
        <label className="field">
          <span>Month</span>
          <select value={month} onChange={(event) => setMonth(event.target.value)}>
            <option value="">Every month</option>
            {(data?.months ?? []).map((one) => (
              <option key={one} value={one}>
                {one}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Person</span>
          <select value={person} onChange={(event) => setPerson(event.target.value)}>
            <option value="">Everyone</option>
            {people.map((one) => (
              <option key={one.id} value={one.id}>
                {one.name || 'Unnamed'}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? <p className="error">{error}</p> : null}

      {data?.truncated ? (
        <p className="footnote">
          Showing the most recent entries only. Narrow by month to see further
          back.
        </p>
      ) : null}

      {shown.length === 0 ? (
        <p className="footnote">Nothing matches.</p>
      ) : (
        shown.map((entry) => (
          <div className={entry.voided ? 'entry voided' : 'entry'} key={entry.id}>
            {/* Whose it was, at a glance: their face, or the family's mark. */}
            <span className="entry-face" aria-hidden="true">
              {(() => {
                const who = people.find((one) => one.id === entry.people[0]);
                return who ? <Character spec={who.avatar} size={36} /> : <FamilyMark size={36} />;
              })()}
            </span>
            <div className="entry-body">
              <p className="entry-reason">{entry.reason || 'No reason recorded'}</p>
              <p className="entry-date">
                {entry.date}
                {entry.people.length
                  ? ` · ${entry.people.map((id) => names.get(id) ?? 'Removed').join(', ')}`
                  : ' · whole family'}
                {entry.voided && entry.voidReason ? ` · taken back: ${entry.voidReason}` : ''}
              </p>
              {(() => {
                // "Split: Added to Bike $2.00. Added to Ravi $8.00." See family/split.ts.
                const line = splitLine(
                  entryParts(entry, (id) => names.get(id) ?? ''),
                  (id) => goals.find((g) => g.id === id)?.title ?? '',
                );
                return line ? <p className="entry-split">{line}</p> : null;
              })()}
              {entry.corrections?.length ? (
                <p className="entry-corrected">
                  Corrected. Before: {entry.corrections.map((before) =>
                    `${dollars(before.amount)}${before.reason ? ` “${before.reason}”` : ''} on ${before.date}`,
                  ).join('; then ')}
                </p>
              ) : null}
              {correcting === entry.id ? (
                <CorrectEntry
                  entry={entry}
                  people={people}
                  busy={busy}
                  onCancel={() => setCorrecting(null)}
                  onSave={(changed) =>
                    void attempt(() => amendEntry(familyId, familyKey, entry.id, changed, people)).then(
                      (saved) => {
                        if (saved) setCorrecting(null);
                      },
                    )
                  }
                />
              ) : null}
              {voiding === entry.id ? (
                <div className="void-box">
                  <input
                    value={voidReason}
                    onChange={(event) => setVoidReason(event.target.value)}
                    placeholder="Why is this being taken back?"
                    maxLength={120}
                    autoComplete="off"
                  />
                  <div className="actions">
                    <button
                      className="primary"
                      type="button"
                      disabled={busy || !voidReason.trim()}
                      onClick={() =>
                        void attempt(() =>
                          voidEntry(familyId, familyKey, entry.id, voidReason),
                        ).then((saved) => {
                          if (saved) {
                            setVoiding(null);
                            setVoidReason('');
                          }
                        })
                      }
                    >
                      Take it back
                    </button>
                    <button type="button" disabled={busy} onClick={() => setVoiding(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : !entry.voided && correcting !== entry.id ? (
                <span className="secret-actions">
                  {/* A goal's spend is not corrected in place: take it back,
                      which reopens the goal, and complete it again. */}
                  {entry.kind !== 'goal' ? (
                    <button
                      type="button"
                      className="text-button"
                      onClick={() => {
                        setCorrecting(entry.id);
                        setVoiding(null);
                      }}
                    >
                      Correct it
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => {
                      setVoiding(entry.id);
                      setVoidReason('');
                      setCorrecting(null);
                    }}
                  >
                    Take it back
                  </button>
                </span>
              ) : null}
            </div>
            <p className={entry.amount < 0 ? 'entry-amount out' : 'entry-amount in'}>
              {dollars(entry.amount)}
            </p>
          </div>
        ))
      )}

      <div className="actions">
        <button
          className="primary"
          type="button"
          disabled={!data || data.entries.length === 0}
          onClick={() => {
            // Built from what is on screen after filtering, so what you export
            // is what you are looking at.
            const csv = buildCsv(rowsFor(shown, people));
            const slug = (familyName || 'valley').toLowerCase().replace(/[^a-z0-9]+/g, '-');
            downloadCsv(csv, `${slug}-history.csv`);
          }}
        >
          Export what is shown
        </button>
        {/* Absent when the journal is a section of Home rather than a
            screen of its own: the navigation bar is the way out then. */}
        {onBack ? (
          <button type="button" onClick={onBack}>
            Back
          </button>
        ) : null}
      </div>

      <p className="footnote">
        The export is built on this device, because the server has no names to
        put in it. It is the one file in this system that holds them in the
        clear &mdash; keep it somewhere you would keep a bank statement.
      </p>
    </>
  );
}

/**
 * Correcting an entry: every part of it, starting from what it says now.
 *
 * Direction is a choice, as when recording, so a spend corrected by a parent
 * cannot turn into money in because a minus sign went missing.
 */
function CorrectEntry({
  entry,
  people,
  busy,
  onSave,
  onCancel,
}: {
  entry: Entry;
  people: Person[];
  busy: boolean;
  onSave: (changed: { amount: string; reason: string; people: string[]; date: string }) => void;
  onCancel: () => void;
}) {
  const [direction, setDirection] = useState<'in' | 'out'>(entry.amount < 0 ? 'out' : 'in');
  const [amount, setAmount] = useState(asTyped(Math.abs(entry.amount)));
  const [reason, setReason] = useState(entry.reason);
  const [date, setDate] = useState(entry.date);
  const [chosen, setChosen] = useState<string[]>(
    entry.people.filter((id) => people.some((person) => person.id === id)),
  );
  const ready = looksLikeAmount(amount) && Number(amount) !== 0 && reason.trim() !== '' && date !== '';

  return (
    <div className="record correct-box">
      <div className="segmented">
        <button type="button" className={direction === 'in' ? 'on' : ''} onClick={() => setDirection('in')}>
          {inLabel()}
        </button>
        <button type="button" className={direction === 'out' ? 'on' : ''} onClick={() => setDirection('out')}>
          Spent
        </button>
      </div>
      <label className="field">
        <span>{howMuch()}</span>
        <input value={amount} onChange={(event) => setAmount(event.target.value)} inputMode={amountInputMode()} autoComplete="off" />
      </label>
      <label className="field">
        <span>What for?</span>
        <input value={reason} onChange={(event) => setReason(event.target.value)} maxLength={120} autoComplete="off" />
      </label>
      <label className="field">
        <span>When?</span>
        <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
      </label>
      {people.length ? (
        <>
          <p className="field-label">Who is it for?</p>
          <div className="chips">
            {people.map((person) => (
              <button
                type="button"
                key={person.id}
                className={chosen.includes(person.id) ? 'chip on' : 'chip'}
                onClick={() =>
                  setChosen((current) =>
                    current.includes(person.id) ? current.filter((id) => id !== person.id) : [...current, person.id],
                  )
                }
              >
                {person.name || 'Unnamed'}
              </button>
            ))}
          </div>
          <p className="footnote">Choose nobody and it belongs to the whole family.</p>
        </>
      ) : null}
      <p className="footnote">What it said before stays on the entry, so the change is visible.</p>
      <div className="actions">
        <button aria-busy={busy}
          type="button"
          className="primary"
          disabled={busy || !ready}
          onClick={() => onSave({ amount: signed(amount, direction), reason: reason.trim(), people: chosen, date })}
        >
          {busy ? 'Saving…' : 'Save the correction'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
